"use no memo";
import { useState } from "react";
import { useShell } from "../app/shell";
import { useAccount } from "../data/session";
import {
  useInviteGroupFriends,
  useStartGroupCall,
  useSubmitting,
} from "../data/mutations";
import { number, t } from "../i18n";
import { Avatar, Modal } from "../ui/components";
import { notify } from "../ui/toast";

// The same optional picker before starting and over a running group call.
export function GroupFriendsDialog({
  link,
  onClose,
}: {
  link?: string;
  onClose?: () => void;
}) {
  const shell = useShell(),
    account = useAccount();
  const invite = useInviteGroupFriends(),
    start = useStartGroupCall(),
    submitting = useSubmitting();
  const [selected, setSelected] = useState<string[]>([]),
    [search, setSearch] = useState(""),
    [error, setError] = useState("");
  const close = onClose ?? shell.close;
  const friends = account?.friends ?? [],
    shown = friends.filter((person) =>
      `${person.name} ${person.username}`
        .toLocaleLowerCase()
        .includes(search.trim().toLocaleLowerCase()),
    );
  const peers = selected.filter((id) =>
    friends.some((friend) => friend.id === id),
  );
  async function send() {
    setError("");
    if (!peers.length) return;
    try {
      if (link) {
        await invite.mutateAsync({ link, peers });
        close();
        notify("أُرسلت الدعوات.", "success");
      } else {
        const entry = await start.mutateAsync();
        start.reset();
        // Starting has succeeded even if sending or capturing the microphone
        // fails. Keep its group link available in the live call.
        try {
          await invite.mutateAsync({ link: entry.link.id, peers });
        } catch (error) {
          notify(error instanceof Error ? error.message : String(error));
        }
        close();
        await shell.groupCall
          .enter(entry)
          .catch((error: unknown) =>
            notify(error instanceof Error ? error.message : String(error)),
          );
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  }
  return (
    <Modal
      title={t("دعوة أصدقاء")}
      onClose={submitting ? undefined : close}
      dismissible={!submitting}
    >
      <h2>{t("دعوة أصدقاء")}</h2>
      <p className="modal-description">
        {t("اختر حتى ١١ صديقًا. تصلهم دعوة للانضمام دون رنين.")}
      </p>
      {!friends.length ? (
        <p className="inline-empty">
          {t("أضف أصدقاء لدعوتهم إلى مكالمة جماعية.")}
        </p>
      ) : (
        <>
          <label className="group-friend-search">
            {t("البحث في الأصدقاء")}
            <input
              data-autofocus
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <ul className="group-friend-list">
            {shown.map((person) => (
              <li key={person.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={selected.includes(person.id)}
                    disabled={
                      submitting ||
                      (!selected.includes(person.id) && selected.length >= 11)
                    }
                    onChange={(event) =>
                      setSelected((ids) =>
                        event.target.checked
                          ? [...ids, person.id]
                          : ids.filter((id) => id !== person.id),
                      )
                    }
                  />
                  <Avatar person={person} />
                  <span>
                    <bdi>{person.name}</bdi>
                    <small>
                      <bdi>@{person.username}</bdi>
                    </small>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {!shown.length && (
            <p className="inline-empty">{t("لا توجد نتائج.")}</p>
          )}
        </>
      )}
      {error && (
        <p className="form-error" role="alert">
          {t(error)}
        </p>
      )}
      <div className="confirm-actions">
        <button
          className="button secondary"
          disabled={submitting}
          onClick={close}
        >
          {t("إلغاء")}
        </button>
        <button
          className="button"
          disabled={!peers.length || submitting}
          aria-busy={submitting}
          onClick={() => void send()}
        >
          {t(link ? "إرسال الدعوات ({count})" : "ابدأ وادعُ ({count})", {
            count: number(peers.length),
          })}
        </button>
      </div>
    </Modal>
  );
}
